(ns frontend.view.entity-viewers.entities-editor
  (:require
   [reagent.core :as r]
   [frontend.actions.entity :as entity-actions]
   [frontend.actions.account :as account-actions]
   [common.localization :as loc]
   [potpuri.core :refer [dissoc-in]]
   [frontend.state.current-session :as session]
   [common.intelligence.common :as ic]
   [frontend.components.field-editor :as field-editor]
   [common.schema.constraints :as constraints]
   [frontend.intelligence.icon :as intelligence-icon]
   [frontend.utils :as fu]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [common.entity :as entity]
   [frontend.state.permissions :as state-permissions]
   [frontend.view.modals.events :as modals]
   [common.schema.registry :as registry]
   [common.schema.field :as field]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [reagent.impl.component :as reagent-component]
   [common.schema.calculated-field :as calculated-field]
   [frontend.routing :as routing]
   [common.schema.entities.pipeline :as pipeline]
   [frontend.view.modals.options-management.view :as options-management]
   [common.entity-utils :as eu]
   [ui.components.button.view :as button]
   [common.util :as cu]))

(defn new-order
  [old-order moved-attribute target]
  (if (or (nil? target) (= moved-attribute target))
    old-order
    (let [order-lookup (->> old-order
                            (map-indexed (fn [i attr] [attr i]))
                            (into {}))
          move-before? (> (order-lookup moved-attribute) (order-lookup target))
          cleaned-order (remove #(= moved-attribute %) old-order)
          head (take-while #(not= % target) cleaned-order)
          tail (rest (drop-while #(not= % target) cleaned-order))
          middle (if move-before?
                   [moved-attribute target]
                   [target moved-attribute])]
      (concat head middle tail))))

(defn debug-sorting
  [ordering remaining fields]
  (js/console.log "FIELDS : " fields)
  (js/console.log "ORDERING : " ordering)
  (js/console.log "DEFAULTS : " remaining)
  fields)

(defn render-editor
  [local-atom
   {:keys [field-schema-lookup
           state-atom
           drag-atom
           drag-id
           ordering
           order-config-path
           default-ordering
           view-id
           visible-fields
           override-show-empty-fields?
           show-empty?
           module-id
           custom-save!
           close-modal!]
    :as   view-args}]
  (let [set-show-empty! (fn [show?] (swap! state-atom assoc :show-empty? show?))
        show-empty? (if override-show-empty-fields?
                      show-empty?
                      (:show-empty? @state-atom))
        default-ordering (concat default-ordering
                                 (->> visible-fields
                                      (map (fn [f]
                                             [(:entity-id f)
                                              (some->> f
                                                       :field-schema
                                                       :attribute)]))
                                      (remove (fn [fo]
                                                (some #{fo} default-ordering)))
                                      (into [])))
        enriched-entities-by-id (->> visible-fields
                                     (group-by :entity-id)
                                     (map (fn [[eid field-datas]]
                                            (when-let [_entity-type (->> field-datas
                                                                         (first)
                                                                         :entity-type)]
                                              [eid
                                               (ic/enrich-entity (->> field-datas
                                                                      (first)
                                                                      :entity-atom
                                                                      (deref)))])))
                                     (into {}))
        order-config-path (or order-config-path
                              [view-id :modules :module-config module-id :ordering])
        saved-ordering (if (coll? ordering) ordering [])
        remaining-default-ordering (->> default-ordering
                                        (remove #(some #{%} saved-ordering))
                                        (into []))
        field-order (concat ordering remaining-default-ordering)
        order-lookup (->> field-order
                          (map-indexed (fn [i attr]
                                         [attr i]))
                          (into {}))
        empty-fields-exist?
        (and
         (not override-show-empty-fields?)
         (->>
          visible-fields
          (some
           (fn [{:keys [entity-id attribute field-schema entity-atom]}]
             (let [entity @entity-atom
                   entity-type (:entity/type entity)
                   enriched-entity (get enriched-entities-by-id entity-id)
                   pipeline-id? (fu/pipeline-id entity)
                   intelligence-value (get enriched-entity attribute)
                   has-access?
                   (state-permissions/field-read-access? attribute entity-type pipeline-id?)]
               (and has-access?
                    (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                      (or (not (fn? in-use-filter?))
                          (in-use-filter? entity
                                          @session/entity-types
                                          session/has-feature-access?
                                          session/has-entity-access?)))
                    (not (:field/secondary-field? field-schema)) ;; secondary fields are shown in related fields in editor
                    (not= (:editor/type field-schema) :editor.type/none)
                    (not= :role/system
                          (->> field-schema
                               :field/default-authorization
                               :access/write))
                    (fu/empty-value? intelligence-value)))))))]
    [:div
     {:key        (str "deal-viewer-id-" @entity-utils/force-remount-atom)
      :class-name (fu/cs "entity-editor")}
     (->>
      visible-fields
      (sort-by (fn [{:keys [entity-id attribute]}]
                 (let [idx (get order-lookup [entity-id attribute])]
                   idx)))
      #_(debug-sorting ordering remaining-default-ordering)
      (map
       (fn [{:keys [entity-id entity-atom attribute field-schema]}]
         (let [entity @entity-atom
               tenant @session/tenant
               tenant-schema @session/tenant-schema
               enriched-entity (get enriched-entities-by-id entity-id)
               entity-type (:entity/type entity)
               pipeline-id? (fu/pipeline-id entity)
               pipeline-attribute (pipeline/pipeline-key entity-type)
               value (fu/get-value tenant tenant-schema entity attribute)
               intelligence-value (get enriched-entity attribute)
               is-intelligence-value? (and (nil? value)
                                           (contains? enriched-entity attribute)
                                           (not (cu/blank-value?
                                                 intelligence-value)))
               has-access? (state-permissions/field-read-access? attribute entity-type pipeline-id?)
               read-only?
               (or (= :field.access/read (:access/type field-schema))
                   (not
                    (state-permissions/field-write-access? attribute entity-type pipeline-id?)))
               no-value? (and (nil? value)
                              (fu/empty-value? intelligence-value))]
           (when (and has-access?
                      (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                        (or (not (fn? in-use-filter?))
                            (in-use-filter? entity
                                            @session/entity-types
                                            session/has-feature-access?
                                            session/has-entity-access?)))
                      (not (and read-only? no-value?))
                      (not (:field/secondary-field? field-schema)) ;; secondary fields are shown in related fields in editor
                      (not= (:editor/type field-schema) :editor.type/none)
                      (or show-empty?
                          (not no-value?)))

             ^{:key attribute}
             [field-editor/view
              (merge
               field-schema
               {:editor/type        (or (when value
                                          (->> field-schema
                                               :field/default-visibility
                                               :non-empty-editor-type))
                                        (:editor/type field-schema))
                :state-atom         state-atom ;; state is passed  from outside so debugging is easier
                :value              (if is-intelligence-value? intelligence-value value)
                :containing-entity  entity
                :class-name         (fu/cs (:class-name field-schema)
                                           (when no-value? "empty"))
                :entity-types-atom  session/entity-types
                :entity-type        entity-type
                :entity-unique-name (entity/unique-name-or-id entity)
                ;; TODO: take language from user / tenant config -> default to :en
                ;; if only :en in field/translations use :en
                :label              (or (->> field-schema
                                             :field/translations
                                             :en)
                                        (loc/tr attribute)
                                        (loc/field-name attribute field-schema))
                ;; we select label outside of field-editor so we can customize label if needed

                ;; can be function or boolean - most likely always view dependent
                :mandatory?         entity-editor/mandatory-field?
                ;; can pass text instead of function too
                :info-text          (fn [_fld]
                                      ;; we can get info text from tenant settings here
                                      )
                :read-only?         read-only?

                ;:field-access [:x :w :r] ;; TODO: Fairly urgent - change the way field editors access is resolved everywhere so that the access is passed from outside
                :open-settings!     (fn []
                                      (modals/open-modal! options-management/modal-key
                                                          {:pipeline-attribute pipeline-attribute
                                                           :selected-field     attribute})
                                      (when close-modal!
                                        (close-modal!)))
                :disabled?          read-only?
                :dragging?          drag-id
                :drop               (:drop @drag-atom)
                :placeholder        (field/placeholder field-schema)
                :drag-options
                (merge
                 {:draggable?     true
                  :on-drag-start! (fn [_arg]
                                    (let [attribute [entity-id attribute]]
                                      (swap! local-atom assoc :drag-id attribute)
                                      (swap! drag-atom assoc :drag-id attribute)))
                  :on-drag-end!   (fn [attribute]
                                    (let [attribute [entity-id attribute]
                                          drop-target (:drag-over @local-atom)
                                          new-order (new-order field-order attribute drop-target)]
                                      (swap! local-atom dissoc :drag-over :drag-id)
                                      (swap! drag-atom dissoc :drag-id)
                                      (when (not= new-order field-order)
                                          ;;optimistic update:
                                        (swap! session/view-config assoc-in
                                               order-config-path
                                               new-order)
                                        (account-actions/*->update-view-config
                                         {:path      order-config-path
                                          :new-value new-order}
                                         identity
                                         (fn [_]
                                             ;;retract the optimistic update above
                                           (when (= new-order
                                                    (get-in @session/view-config
                                                            order-config-path))
                                             (swap! session/view-config assoc-in
                                                    order-config-path
                                                    field-order)))))))
                  :on-drag-leave  (fn [attribute]
                                    (let [attribute [entity-id attribute]]
                                      (when (and (not= attribute (:drag-id @local-atom))
                                                 (= attribute (:drag-over @local-atom)))
                                        (swap! local-atom dissoc :drag-over))))
                  :on-drag-enter  (fn [attribute]
                                    (let [attribute [entity-id attribute]
                                          dragging-order (order-lookup drag-id)
                                          drag-enter-order (order-lookup attribute)]
                                      (when (not= attribute (:drag-id @local-atom))
                                        (swap! local-atom assoc :drag-over attribute)
                                        (if (> dragging-order drag-enter-order)
                                          (swap! drag-atom assoc :drop :above)
                                          (swap! drag-atom assoc :drop :below)))))} ;; drag callbacks here too
                 (:drag-options view-args))
                ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
                :tools              (concat [:tool-item/cancel :tool-item/save :tool-item/edit :tool-item/copy
                                             :tool-item/open-url :tool-item/clear :tool-item/debug]
                                            (when (and (session/is-admin?)
                                                       (->> field-schema
                                                            :named-value-attribute))
                                              [:tool-item/settings]))
                :validate!
                (fn [attribute value]
                  (let [entity @entity-atom
                        schema (get field-schema-lookup [entity-id attribute])
                        field-constraints (:field/constraints schema)
                        errors (constraints/validate
                                field-constraints
                                (:db/id entity)
                                value
                                entity
                                (partial session/unique-check false (partial routing/open-entity))
                                loc/tr
                                logging/warn)]
                    errors))
                :error-text         nil ;; TODO: use editor-atom to store validation and save errors
                :on-save!           (fn [attribute new-value on-success! on-error!]
                                      (let [entity @entity-atom
                                            value (fu/get-value tenant tenant-schema entity attribute)]
                                        (when (or (not (fn? custom-save!))
                                                  (not
                                                   (custom-save! attribute new-value on-success! on-error!)))
                                          ;; custom
                                          ;;make sure local state is cleared when no changes:
                                          (if (and (= new-value value)
                                                   (fn? on-success!))
                                            (on-success!)
                                            (when-not (= new-value value)
                                              (if-let [create-value (:new-option new-value)]
                                                (entity-actions/*->field-update-with-new-entity!
                                                 (:db/id enriched-entity)
                                                 attribute
                                                 value
                                                 create-value
                                                 (fn [_res]
                                                   (when on-success! (on-success!)))
                                                 (fn [err]
                                                   (when on-error! (on-error! err))))
                                                (do
                                                  ;;optimistic save to frontend db:
                                                  (swap! entity-atom assoc attribute new-value)
                                                  (deref entity-atom)
                                                  (entity-actions/*->field-update!
                                                   (:db/id enriched-entity)
                                                   attribute
                                                   new-value
                                                   (fn [_res]
                                                     (when on-success!
                                                       (on-success!)))
                                                   (fn [err]
                                                     ;;retract the optimistic save above
                                                     (when (= new-value (get @entity-atom attribute))
                                                       (swap! entity-atom assoc attribute value)
                                                       (deref entity-atom))
                                                     (on-error! err))))))))))}
               (when is-intelligence-value? ;; we decide source outside
                 {:source-options {:source-icon-fn intelligence-icon/view}})
               (when-let [secondary-attribute (:field/secondary-attribute field-schema)] ;; secondary attribute
                 {:secondary-view-args
                  (let [sec-templ (get field-schema-lookup [entity-id secondary-attribute])
                        secondary-field-schema (registry/get-field-schema tenant-schema
                                                                          secondary-attribute)]
                    (merge
                     sec-templ
                     {:state-atom state-atom
                      :value      (if-let [_composite-attributes (:field/composite-attributes
                                                                  secondary-field-schema)]
                                    (when-let [calc-function (:field/calculated-field
                                                              secondary-field-schema)]
                                      (calculated-field/calculate
                                       {:calc-fn          calc-function
                                        :entity-data      entity
                                        :entity-lookup    session/get-entity
                                        :reverse-lookup   session/reverse-lookup
                                        :tr               loc/tr
                                        :bigdec->float    fu/bigdec->float
                                        :resolve-value    (partial eu/get-value
                                                                   tenant
                                                                   tenant-schema)
                                        :float->bigdec    fu/float->bigdec
                                        :get-field-schema (partial registry/get-field-schema
                                                                   tenant-schema)
                                        :display-string   entity/display-string}))
                                    (or (get @entity-atom secondary-attribute)
                                        (when (= (:field/data-type sec-templ)
                                                 :data.type/currency-symbol)
                                          (:tenant/default-currency @session/tenant))))
                      :label      (loc/tr secondary-attribute) ;; when we have custom dynamic fields the labels come from tenant configuration
                      :mandatory? entity-editor/mandatory-field?
                      :error-text nil}))}))]))))
      (doall))
     (when (and (not (session/is-read-only-user?))
                empty-fields-exist?)
       [:div
        {:class "*:m-0 *:text-sm"}
        [button/primary-open
         {:class "text-xs"
          :on-click   (fn [e]
                        (.preventDefault e)
                        (.stopPropagation e)
                        (set-show-empty! (not show-empty?)))}
         (if show-empty?
           (loc/tr :hide-empty-fields)
           (loc/tr :show-empty-fields))]])]))

(defn view
  [{:keys [_state-atom _drag-atom entity-ids remove-fields
           default-ordering override-editor-types read-only-fields]
    :as   _args}]
  (let [first-id (first entity-ids)
        editor-atom (r/cursor app-db/app-state [:entity-editor first-id])
        drag-atom (r/cursor app-db/app-state [:entity-editor :drag first-id])
        local-atom (atom {})
        tenant-schema @session/tenant-schema
        account @session/account
        role @session/role]
    (r/create-class
     {:component-will-unmount (fn []
                                (swap! editor-atom dissoc-in [:entity-editor first-id]))
      :component-did-update   (fn [this old-argv]
                                (let [prev-entity-ids (-> old-argv
                                                          reagent-component/extract-props
                                                          :entity-ids)
                                      entity-ids (-> this
                                                     r/props
                                                     :entity-ids)]
                                  (when-not (= prev-entity-ids entity-ids)
                                    (entity-utils/force-remount!))))
      :reagent-render
      (fn [args]
        (let [containing-entity (:containing-entity args)
              pipeline-id (fu/pipeline-id containing-entity)
              visible-fields (->> entity-ids
                                  (mapcat
                                   (fn [eid]
                                     (let [entity-atom (r/cursor session/entities [eid])
                                           entity @entity-atom
                                           entity-type (:entity/type entity)]
                                       (->> (entity-editor/resolve-template
                                             {:entity           entity
                                              :tenant-schema    tenant-schema
                                              :account          account
                                              :role             role
                                              :pipeline-id      pipeline-id
                                              :remove-fields    (get remove-fields eid)
                                              :override-editor-types override-editor-types
                                              :read-only-fields read-only-fields})
                                            (map (fn [field-schema]
                                                   {:attribute    (:attribute field-schema)
                                                    :field-schema field-schema
                                                    :entity-type  entity-type
                                                    :entity-atom  entity-atom
                                                    :entity-id    eid}))
                                            (doall))))))
              field-schema-lookup (->> visible-fields
                                       (map (fn [field-info]
                                              [[(:entity-id field-info) (:attribute field-info)]
                                               (:field-schema field-info)]))
                                       (into {}))
              default-ordering (or default-ordering
                                   (->> visible-fields
                                        (map (fn [{:keys [entity-id attribute]}]
                                               [entity-id attribute]))
                                        (doall)))]
          [render-editor local-atom
           (merge args
                  {:drag-id             (:drag-id @drag-atom)
                   :drag-atom           drag-atom
                   :visible-fields      visible-fields
                   :default-ordering    default-ordering
                   :field-schema-lookup field-schema-lookup
                   :editor-atom         editor-atom
                   :state-atom          editor-atom
                   :pipeline-id         pipeline-id})]))})))
